import React, { useCallback, useState } from 'react'
import styled from 'styled-components'

// Only render in development with REACT_APP_FORK_MODE enabled
const FORK_MODE =
  process.env.NODE_ENV === 'development' && process.env.REACT_APP_FORK_MODE === 'true'

// Proxy URLs (served by setupProxy.js in development)
const FORK_NETWORKS = {
  polygon: {
    chainId: 137,
    chainIdHex: '0x89',
    chainName: 'Fork Polygon',
    rpcUrl: '/fork-rpc/polygon',
    symbol: 'MATIC',
    decimals: 18,
    blockExplorer: 'https://polygonscan.com'
  },
  bnb: {
    chainId: 56,
    chainIdHex: '0x38',
    chainName: 'Fork BNB',
    rpcUrl: '/fork-rpc/bnb',
    symbol: 'BNB',
    decimals: 18,
    blockExplorer: 'https://bscscan.com'
  }
}

const DevToolsContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const DevBadge = styled.div`
  background: #ff6b00;
  color: white;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: #e65c00;
  }
`

const DevPanel = styled.div<{ open: boolean }>`
  display: ${({ open }) => (open ? 'flex' : 'none')};
  flex-direction: column;
  gap: 8px;
  background: ${({ theme }) => theme.bg2};
  border: 1px solid ${({ theme }) => theme.bg3};
  border-radius: 12px;
  padding: 16px;
  min-width: 200px;
`

const PanelTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.text1};
  margin-bottom: 8px;
`

const StatusText = styled.div<{ error?: boolean }>`
  font-size: 11px;
  color: ${({ error, theme }) => (error ? '#dc3545' : theme.text3)};
  text-align: center;
`

const RpcUrl = styled.div`
  background: ${({ theme }) => theme.bg1};
  border: 1px solid ${({ theme }) => theme.bg3};
  border-radius: 6px;
  padding: 8px;
  font-size: 11px;
  font-family: monospace;
  word-break: break-all;
  color: ${({ theme }) => theme.text2};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.bg3};
  }
`

const Instructions = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.text3};
  line-height: 1.4;
`

const NetworkRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;
`

const AddButton = styled.button<{ status?: 'success' | 'error' }>`
  background: ${({ status, theme }) =>
    status === 'success' ? '#28a745' : status === 'error' ? '#dc3545' : theme.primary1};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }
`

interface NetworkStatus {
  polygon: 'idle' | 'checking' | 'online' | 'offline'
  bnb: 'idle' | 'checking' | 'online' | 'offline'
}

export default function ForkDevTools() {
  const [open, setOpen] = useState(false)
  const [status, setStatus] = useState<NetworkStatus>({ polygon: 'idle', bnb: 'idle' })
  const [copied, setCopied] = useState<string | null>(null)
  const [addStatus, setAddStatus] = useState<{ [key: string]: 'success' | 'error' | undefined }>({})

  const checkNetwork = useCallback(async (network: 'polygon' | 'bnb') => {
    setStatus(prev => ({ ...prev, [network]: 'checking' }))
    try {
      const response = await fetch(FORK_NETWORKS[network].rpcUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jsonrpc: '2.0', method: 'eth_blockNumber', params: [], id: 1 })
      })
      const data = await response.json()
      setStatus(prev => ({ ...prev, [network]: data.result ? 'online' : 'offline' }))
    } catch {
      setStatus(prev => ({ ...prev, [network]: 'offline' }))
    }
  }, [])

  const copyRpcUrl = useCallback((network: 'polygon' | 'bnb') => {
    const config = FORK_NETWORKS[network]
    const rpcUrl = `${window.location.origin}${config.rpcUrl}`
    navigator.clipboard.writeText(rpcUrl)
    setCopied(network)
    setTimeout(() => setCopied(null), 2000)
  }, [])

  const addNetworkToWallet = useCallback(async (network: 'polygon' | 'bnb') => {
    const config = FORK_NETWORKS[network]
    const ethereum = (window as any).ethereum
    if (!ethereum) {
      setAddStatus(prev => ({ ...prev, [network]: 'error' }))
      setTimeout(() => setAddStatus(prev => ({ ...prev, [network]: undefined })), 2000)
      return
    }

    try {
      const rpcUrl = `${window.location.origin}${config.rpcUrl}`
      await ethereum.request({
        method: 'wallet_addEthereumChain',
        params: [
          {
            chainId: config.chainIdHex,
            chainName: config.chainName,
            nativeCurrency: {
              name: config.symbol,
              symbol: config.symbol,
              decimals: config.decimals
            },
            rpcUrls: [rpcUrl],
            blockExplorerUrls: [config.blockExplorer]
          }
        ]
      })
      setAddStatus(prev => ({ ...prev, [network]: 'success' }))
      setTimeout(() => setAddStatus(prev => ({ ...prev, [network]: undefined })), 2000)
    } catch (err) {
      console.error('Failed to add network:', err)
      setAddStatus(prev => ({ ...prev, [network]: 'error' }))
      setTimeout(() => setAddStatus(prev => ({ ...prev, [network]: undefined })), 2000)
    }
  }, [])

  const handleToggle = useCallback(() => {
    setOpen(prev => {
      if (!prev) {
        // Check network status when opening
        checkNetwork('polygon')
        checkNetwork('bnb')
      }
      return !prev
    })
  }, [checkNetwork])

  // Don't render anything if not in fork mode (after all hooks)
  if (!FORK_MODE) {
    return null
  }

  const getStatusIcon = (networkStatus: string) => {
    switch (networkStatus) {
      case 'checking':
        return '⏳'
      case 'online':
        return '🟢'
      case 'offline':
        return '🔴'
      default:
        return '⚪'
    }
  }

  const getRpcUrl = (network: 'polygon' | 'bnb') =>
    `${window.location.origin}${FORK_NETWORKS[network].rpcUrl}`

  return (
    <DevToolsContainer>
      <DevPanel open={open}>
        <PanelTitle>Fork Networks</PanelTitle>

        <div>
          <StatusText>{getStatusIcon(status.polygon)} Polygon Fork (Chain ID: 137)</StatusText>
          <NetworkRow>
            <RpcUrl onClick={() => copyRpcUrl('polygon')} title="Click to copy">
              {copied === 'polygon' ? 'Copied!' : getRpcUrl('polygon')}
            </RpcUrl>
            <AddButton onClick={() => addNetworkToWallet('polygon')} status={addStatus.polygon}>
              {addStatus.polygon === 'success' ? '✓' : addStatus.polygon === 'error' ? '✗' : 'Add'}
            </AddButton>
          </NetworkRow>
        </div>

        <div>
          <StatusText>{getStatusIcon(status.bnb)} BNB Fork (Chain ID: 56)</StatusText>
          <NetworkRow>
            <RpcUrl onClick={() => copyRpcUrl('bnb')} title="Click to copy">
              {copied === 'bnb' ? 'Copied!' : getRpcUrl('bnb')}
            </RpcUrl>
            <AddButton onClick={() => addNetworkToWallet('bnb')} status={addStatus.bnb}>
              {addStatus.bnb === 'success' ? '✓' : addStatus.bnb === 'error' ? '✗' : 'Add'}
            </AddButton>
          </NetworkRow>
        </div>

        <Instructions>
          {status.polygon === 'offline' && status.bnb === 'offline' ? (
            <>Start fork with: anvil --fork-url RPC_URL --port 8137</>
          ) : (
            <>
              To use: In MetaMask, edit Polygon/BNB network and replace RPC URL with the one above
            </>
          )}
        </Instructions>
      </DevPanel>

      <DevBadge onClick={handleToggle}>Fork Mode {open ? '▼' : '▲'}</DevBadge>
    </DevToolsContainer>
  )
}
